import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api.js';

// ========== Auth ==========

export interface MeUser {
  id: string;
  email: string;
  name: string | null;
  admin: 'superadmin' | 'support' | 'finance' | null;
  expires_at: string;
}

export interface BootstrapStatus {
  bootstrap_complete: boolean;
  needs_first_user: boolean;
  needs_admin: boolean;
  needs_integrations: boolean;
}

export function useBootstrapStatus() {
  return useQuery({
    queryKey: ['bootstrap'],
    queryFn: () => api<BootstrapStatus>('/bootstrap/status'),
    staleTime: 30_000,
  });
}

export function useGoogleAuthStatus() {
  return useQuery({
    queryKey: ['google-auth-status'],
    queryFn: () => api<{ enabled: boolean }>('/auth/google/status'),
    staleTime: 5 * 60_000,
  });
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await api<MeUser>('/auth/me');
      } catch {
        return null;
      }
    },
    staleTime: 60_000,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { email: string; password: string }) =>
      api<MeUser>('/auth/login', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useRegister() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      email: string;
      password: string;
      name: string;
      consents: { tos: true; privacy: true; marketing: boolean; ai_processing: boolean };
    }) => api<MeUser & { isFirstUser: boolean; isSuperadmin: boolean }>('/auth/register', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api('/auth/logout', { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['me'] }),
  });
}

// ========== Workspaces ==========

export interface Workspace {
  id: string;
  slug: string;
  name: string;
  role: 'owner' | 'admin' | 'editor' | 'viewer';
  ownerId: string;
  createdAt: string;
}

export function useWorkspaces() {
  return useQuery({
    queryKey: ['workspaces'],
    queryFn: () => api<{ workspaces: Workspace[] }>('/workspaces'),
  });
}

export function useCreateWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; slug: string }) =>
      api<Workspace>('/workspaces', { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspaces'] }),
  });
}

// ========== Documents ==========

export interface Document {
  id: string;
  workspaceId: string;
  parentId: string | null;
  title: string;
  icon: string | null;
  color: string | null;
  category: 'strategy' | 'idea' | 'execution';
  type: 'idea' | 'text' | 'image' | 'url' | 'universe' | 'doc';
  isChildPage: boolean;
  content: { type: 'doc'; content?: unknown[] } | null;
  position: { x: number; y: number; size: number; locked: boolean } | null;
  createdAt: string;
  updatedAt: string;
}

export interface GraphLinkDTO {
  id: string;
  workspaceId: string;
  sourceId: string;
  targetId: string;
  type: 'synapse' | 'wiki';
  strength: number;
}

export function useDocuments(workspaceId: string | undefined, parentId?: string | null, all?: boolean) {
  return useQuery({
    enabled: Boolean(workspaceId),
    queryKey: ['documents', workspaceId, all ? 'all' : (parentId ?? 'root')],
    queryFn: () => {
      const params = new URLSearchParams();
      if (all) params.set('all', '1');
      else if (parentId) params.set('parentId', parentId);
      params.set('limit', '500');
      return api<{ documents: Document[] }>(`/workspaces/${workspaceId}/documents?${params}`);
    },
    refetchOnMount: 'always',
  });
}

export function useLinks(workspaceId: string | undefined) {
  return useQuery({
    enabled: Boolean(workspaceId),
    queryKey: ['links', workspaceId],
    queryFn: () => api<{ links: GraphLinkDTO[] }>(`/workspaces/${workspaceId}/links`),
    refetchOnMount: 'always',
  });
}

export function useCreateDocument(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: Partial<Omit<Document, 'id' | 'workspaceId' | 'createdAt' | 'updatedAt'>>) =>
      api<Document>(`/workspaces/${workspaceId}/documents`, { method: 'POST', body: input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', workspaceId] }),
  });
}

export function useUpdateDocument(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Partial<Document> }) =>
      api<Document>(`/documents/${id}`, { method: 'PATCH', body: patch }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', workspaceId] }),
  });
}

export function useDeleteDocument(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/documents/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents', workspaceId] }),
  });
}

export function useCreateLink(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { sourceId: string; targetId: string; type?: 'synapse' | 'wiki'; strength?: number }) =>
      api<GraphLinkDTO>(`/workspaces/${workspaceId}/links`, {
        method: 'POST',
        body: { type: 'synapse', strength: 1, ...input },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['links', workspaceId] }),
  });
}

export function useDeleteLink(workspaceId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api(`/links/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['links', workspaceId] }),
  });
}
